import React, { useState, useEffect } from 'react';
import { FiPlus, FiMessageSquare, FiX } from 'react-icons/fi';
import '../../styles/Admin/SupportTickets.css';
import { getCurrentUser, createSupportTicket, getUserTickets, addTicketMessage } from '../../lib/supabase/helpers';

function SupportTickets() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterPriority, setFilterPriority] = useState('ALL');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [ticketForm, setTicketForm] = useState({
    subject: '',
    priority: 'medium',
    category: 'general',
    message: ''
  });

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const user = await getCurrentUser();
      
      if (!user) {
        console.log('No user logged in');
        setLoading(false);
        return;
      }

      setCurrentUser(user);

      const userTickets = await getUserTickets(user.user.id);
      console.log('Fetched tickets:', userTickets);
      
      setTickets(userTickets || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setTicketForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser) {
      alert('Please login first');
      return;
    }

    if (!ticketForm.subject.trim() || !ticketForm.message.trim()) {
      alert('Please fill in subject and message');
      return;
    }

    setSubmitting(true);

    try {
      console.log('Creating ticket with data:', {
        userId: currentUser.user.id,
        subject: ticketForm.subject,
        category: ticketForm.category,
        priority: ticketForm.priority
      });

      // Create ticket
      const ticket = await createSupportTicket({
        userId: currentUser.user.id,
        subject: ticketForm.subject,
        category: ticketForm.category,
        priority: ticketForm.priority
      });

      if (!ticket) {
        throw new Error('Failed to create ticket');
      }

      console.log('Ticket created:', ticket);

      // Add initial message
      await addTicketMessage({
        ticketId: ticket.id,
        userId: currentUser.user.id,
        message: ticketForm.message,
        attachments: []
      });

      console.log('Initial message added');

      alert('✅ Ticket created successfully! Our support team will respond soon.');
      setShowCreateModal(false);
      
      // Reset form
      setTicketForm({
        subject: '',
        priority: 'medium',
        category: 'general',
        message: ''
      });

      // Refresh tickets
      await fetchTickets();
    } catch (error) {
      console.error('Error creating ticket:', error);
      alert('❌ Failed to create ticket: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewTicket = (ticket) => {
    console.log('Viewing ticket:', ticket);
    setSelectedTicket(ticket);
    setShowViewModal(true);
    setNewMessage('');
  };

  const handleAddMessage = async () => {
    if (!newMessage.trim() || !selectedTicket || !currentUser) {
      alert('Please enter a message');
      return;
    }

    try {
      console.log('Adding message to ticket:', selectedTicket.id);
      
      await addTicketMessage({
        ticketId: selectedTicket.id,
        userId: currentUser.user.id,
        message: newMessage,
        attachments: []
      });

      setNewMessage('');
      
      // Refresh tickets
      await fetchTickets();
      
      // Update selected ticket with new messages
      const updatedTickets = await getUserTickets(currentUser.user.id);
      const updatedTicket = updatedTickets.find(t => t.id === selectedTicket.id);
      setSelectedTicket(updatedTicket);
      
      alert('✅ Message sent successfully!');
    } catch (error) {
      console.error('Error adding message:', error);
      alert('❌ Failed to send message: ' + error.message);
    }
  };

  const handleClear = () => {
    setFilterStatus('ALL');
    setFilterPriority('ALL');
  };

  const getStatusColor = (status) => {
    const colors = {
      'open': '#3b82f6',
      'in_progress': '#f59e0b',
      'resolved': '#10b981',
      'closed': '#6b7280'
    };
    return colors[status] || '#6b7280';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'low': '#10b981',
      'medium': '#f59e0b',
      'high': '#ef4444',
      'urgent': '#dc2626'
    };
    return colors[priority] || '#f59e0b';
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesStatus = filterStatus === 'ALL' || ticket.status === filterStatus.toLowerCase();
    const matchesPriority = filterPriority === 'ALL' || ticket.priority === filterPriority.toLowerCase();
    return matchesStatus && matchesPriority;
  });

  if (loading) {
    return (
      <div className="support-tickets-page">
        <div className="loading-container" style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '50vh',
          fontSize: '18px'
        }}>
          Loading support tickets...
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="support-tickets-page">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '50vh',
          flexDirection: 'column',
          gap: '20px'
        }}>
          <h2>Please login to view support tickets</h2>
          <button 
            onClick={() => window.location.href = '/login'}
            style={{
              padding: '10px 20px',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="support-tickets-page">
      {/* Header */}
      <div className="tickets-header">
        <div>
          <h1>Support Tickets</h1>
          <p>Create and manage your support requests</p>
        </div>
        <button className="btn-create-ticket" onClick={() => setShowCreateModal(true)}>
          <FiPlus size={20} />
          Create New Ticket
        </button>
      </div>

      {/* Main Container */}
      <div className="tickets-container">
        {/* Filters */}
        <div className="tickets-filters">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="ALL">All Status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>

          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="filter-select"
          >
            <option value="ALL">All Priority</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>

          <button className="btn-clear" onClick={handleClear}>
            Clear Filters
          </button>
        </div>

        {/* Ticket Details Section */}
        <div className="ticket-details-section">
          <div className="section-title-row">
            <h2>Your Tickets ({filteredTickets.length})</h2>
          </div>

          {/* Tickets Table */}
          <div className="tickets-table-container">
            <table className="tickets-table">
              <thead>
                <tr>
                  <th>Ticket ID</th>
                  <th>Subject</th>
                  <th>Category</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Messages</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredTickets.length > 0 ? (
                  filteredTickets.map((ticket) => (
                    <tr key={ticket.id}>
                      <td style={{ fontFamily: 'monospace', fontWeight: '600' }}>
                        #{ticket.id.substring(0, 8)}
                      </td>
                      <td>{ticket.subject}</td>
                      <td style={{ textTransform: 'capitalize' }}>{ticket.category}</td>
                      <td>
                        <span
                          className="status-badge"
                          style={{
                            backgroundColor: `${getPriorityColor(ticket.priority)}20`,
                            color: getPriorityColor(ticket.priority),
                            textTransform: 'capitalize'
                          }}
                        >
                          {ticket.priority}
                        </span>
                      </td>
                      <td>
                        <span
                          className="status-badge"
                          style={{
                            backgroundColor: `${getStatusColor(ticket.status)}20`,
                            color: getStatusColor(ticket.status),
                            textTransform: 'capitalize'
                          }}
                        >
                          {ticket.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td>{new Date(ticket.created_at).toLocaleDateString()}</td>
                      <td>{ticket.ticket_messages?.length || 0} messages</td>
                      <td>
                        <button
                          className="btn-view"
                          onClick={() => handleViewTicket(ticket)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="no-data">
                      {filterStatus !== 'ALL' || filterPriority !== 'ALL' 
                        ? 'No tickets match your filters'
                        : 'You have not created any support tickets yet'
                      }
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create Ticket Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Ticket</h2>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>
                <FiX size={24} />
              </button>
            </div>

            <form className="ticket-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Subject *</label>
                <input
                  type="text"
                  name="subject"
                  value={ticketForm.subject}
                  onChange={handleFormChange}
                  placeholder="Brief description of your issue"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Priority *</label>
                  <select
                    name="priority"
                    value={ticketForm.priority}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Category *</label>
                  <select
                    name="category"
                    value={ticketForm.category}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="general">General</option>
                    <option value="withdrawal">Withdrawal Issue</option>
                    <option value="deposit">Deposit Issue</option>
                    <option value="kyc">KYC Related</option>
                    <option value="technical">Technical Issue</option>
                    <option value="account">Account Related</option>
                    <option value="trading">Trading Issue</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Message *</label>
                <textarea
                  name="message"
                  value={ticketForm.message}
                  onChange={handleFormChange}
                  placeholder="Describe your issue in detail..."
                  rows="6"
                  required
                ></textarea>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowCreateModal(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={submitting}
                >
                  {submitting ? 'Creating...' : 'Create Ticket'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Ticket Modal */}
      {showViewModal && selectedTicket && (
        <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
          <div
            className="modal-content"
            style={{ maxWidth: '800px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <h2>Ticket #{selectedTicket.id.substring(0, 8)}</h2>
                <p style={{ margin: '5px 0 0 0', color: '#6b7280', fontSize: '14px' }}>
                  {selectedTicket.subject}
                </p>
              </div>
              <button className="modal-close" onClick={() => setShowViewModal(false)}>
                <FiX size={24} />
              </button>
            </div>

            {/* Ticket Info Bar */}
            <div style={{
              padding: '15px 30px',
              background: '#f9fafb',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              gap: '20px',
              flexWrap: 'wrap'
            }}>
              <div>
                <strong>Status:</strong>{' '}
                <span
                  className="status-badge"
                  style={{
                    backgroundColor: `${getStatusColor(selectedTicket.status)}20`,
                    color: getStatusColor(selectedTicket.status),
                    textTransform: 'capitalize',
                    marginLeft: '5px'
                  }}
                >
                  {selectedTicket.status.replace('_', ' ')}
                </span>
              </div>
              <div>
                <strong>Priority:</strong>{' '}
                <span
                  className="status-badge"
                  style={{
                    backgroundColor: `${getPriorityColor(selectedTicket.priority)}20`,
                    color: getPriorityColor(selectedTicket.priority),
                    textTransform: 'capitalize',
                    marginLeft: '5px'
                  }}
                >
                  {selectedTicket.priority}
                </span>
              </div>
              <div>
                <strong>Category:</strong> {selectedTicket.category}
              </div>
              <div>
                <strong>Created:</strong> {new Date(selectedTicket.created_at).toLocaleDateString()}
              </div>
            </div>

            {/* Messages */}
            <div style={{ padding: '30px', maxHeight: '400px', overflowY: 'auto' }}>
              <h3 style={{ marginBottom: '20px', fontSize: '18px' }}>Messages</h3>
              
              {selectedTicket.ticket_messages && selectedTicket.ticket_messages.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {selectedTicket.ticket_messages.map((msg) => (
                    <div
                      key={msg.id}
                      style={{
                        padding: '15px',
                        borderRadius: '8px',
                        background: msg.user_id === currentUser.user.id ? '#f3f4f6' : '#dbeafe'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '8px',
                        fontSize: '14px'
                      }}>
                        <strong>
                          {msg.user_id === currentUser.user.id ? 'You' : 'Support Team'}
                        </strong>
                        <span style={{ color: '#6b7280' }}>
                          {new Date(msg.created_at).toLocaleString()}
                        </span>
                      </div>
                      <div style={{ color: '#374151', lineHeight: '1.6' }}>
                        {msg.message}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>
                  No messages yet
                </p>
              )}
            </div>

            {/* Add Reply */}
            {selectedTicket.status !== 'closed' && selectedTicket.status !== 'resolved' && (
              <div style={{
                padding: '20px 30px',
                borderTop: '1px solid #e5e7eb'
              }}>
                <h4 style={{ marginBottom: '10px', fontSize: '16px' }}>Add Reply</h4>
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your message..."
                  rows="4"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    marginBottom: '10px'
                  }}
                />
                <button
                  className="btn-submit"
                  onClick={handleAddMessage}
                  disabled={!newMessage.trim()}
                  style={{ width: 'auto', padding: '10px 20px' }}
                >
                  <FiMessageSquare size={18} style={{ marginRight: '8px' }} />
                  Send Reply
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SupportTickets;